import type { OnDestroy } from '@angular/core';
import { Component, ElementRef, HostListener, inject, Input, ViewChild, type OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import type { SafeHtml } from '@angular/platform-browser';
import { CouponQueryResolverService } from '@core-mkt/services/coupon-query-resolver/coupon-query-resolver.service';
import { CouponUseCases } from '@core-mkt/services/coupon-query-resolver/coupon-use-cases';
import { EnvService } from '@core-mkt/services/env/env.service';
import { ErrorHandlerService } from '@core-mkt/services/error-handler/error-handler.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { XGritApiService } from '@core-mkt/services/xgrit-api/xgrit-api.service';
import type {
  ProductCodeLists,
  XgritPurchaseReceiptMultipriceResponse,
} from '@core-mkt/services/xgrit-api/xgrit-purchase-receipt-price-response';
import { faSearch, faSquareCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ProductTypes, type LineItemList } from '@xcc-models';
import type { VisionButton } from '../../vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '../../vision-button/vision-button/vision-button.component';
import type { WebinarScheduleResponse } from '../../webinar-schedule/webinar-schedule';
import type { BundleBuildCraftCards, BundleSelectionMergedProduct } from './bundle-builder';
import { BundleBuilderForm } from './bundle-builder';
import { BundleProductCardComponent } from '../bundle-product-card/bundle-product-card.component';
import { LoadingIndicatorComponent } from '@core-mkt/shared/components/loading-indicator/loading-indicator.component';

@Component({
  selector: 'bundle-builder',
  templateUrl: './bundle-builder.component.html',
  styleUrls: ['./bundle-builder.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    FontAwesomeModule,
    VisionButtonComponent,
    BundleProductCardComponent,
    LoadingIndicatorComponent,
  ],
})
export class BundleBuilderComponent implements OnInit, OnDestroy {
  @Input() data: BundleBuilderForm;
  @ViewChild('searchToggle') searchToggle: ElementRef<HTMLElement>;
  @ViewChild('searchDropdown', { static: false }) searchDropdown: ElementRef<HTMLElement>;
  @ViewChild('modal', { static: false }) modal: ElementRef<HTMLElement>;
  @ViewChild('modalCTA', { static: false }) modalCTA: ElementRef<HTMLElement>;
  formTitle: SafeHtml;
  topContent: SafeHtml;
  productListTitle: SafeHtml;
  requirementsTitle: SafeHtml;
  electivesTitle: SafeHtml;
  recommendedProductContent: SafeHtml;
  theme: ThemeInterface;
  faSearch = faSearch;
  faSquareCheck = faSquareCheck;
  faTimes = faTimes;
  showBundleSearch = false;
  showFiltering = false;
  showModal = false;
  hideScheduleCta = false;
  showBundleProductList = false;
  emptyStateMessage = '';
  checkoutSelectedProducts: BundleSelectionMergedProduct[] = [];
  checkoutUrl = '';
  deliveryMethod = '';
  searchTerm = '';
  formSelectedOptions: BundleBuildCraftCards[] = [];
  builderIndex: number;

  globalCDIQueryParam: string;
  globalSalesQueryParam: string;

  private _bundleHours = 0;
  private _bundleOptions: BundleBuildCraftCards[] = [];
  private _electiveClasses: BundleSelectionMergedProduct[] = [];
  private _checkoutProductIds: string[] = [];
  private _requiredClassProductList: BundleSelectionMergedProduct[] = [];
  private _selectedBaseProduct: BundleSelectionMergedProduct | null = null;
  private _totalCost = 0;
  private _requiredHours = 0;
  private checkoutNavigationStarted = false;
  private readonly pageShowHandler = (event: PageTransitionEvent): void => {
    if (event.persisted && this.checkoutNavigationStarted) {
      this.resetBundleBuilderForm();
    }
  };

  private xgritApi = inject(XGritApiService);
  private redactor = inject(WysiwygRedactorService);
  private themeParser = inject(ThemeParserService);
  private env = inject(EnvService);
  private errorHandler = inject(ErrorHandlerService);
  private couponQueryResolverService = inject(CouponQueryResolverService);

  constructor() {
    this.checkoutUrl = this.env.get.xgritCheckoutUrl;

    this.globalCDIQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.CDI);
    this.globalSalesQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.SALES);
  }

  @HostListener('document:click', ['$event'])
  click(event: MouseEvent) {
    const target = event.target as HTMLElement;
    //if the event target is not the searchToggle button or the searchDropdown element/children, then close the dropdown
    if (
      target !== this.searchToggle?.nativeElement &&
      this.searchDropdown?.nativeElement &&
      !this.searchDropdown.nativeElement.contains(target)
    ) {
      this.searchTerm = '';
      this.updateBundleOptions();
      this.toggleBundleSearch('close');
    }
    //if the event target is not the modal and not the modalCTA then close the modal
    if (
      this.modalCTA?.nativeElement &&
      this.modal?.nativeElement &&
      this.showModal &&
      !this.modalCTA.nativeElement.contains(target) &&
      !this.modal.nativeElement.contains(target)
    ) {
      this.showModal = false;
    }
  }

  //also close modal or search if the escape key is pressed
  @HostListener('document:keydown', ['$event'])
  keydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.showModal = false;
      this.toggleBundleSearch('close');
    }
  }

  get bundleHours(): number {
    return this._bundleHours;
  }

  get bundleOptions(): BundleBuildCraftCards[] {
    return this._bundleOptions;
  }

  get checkoutButtonData(): VisionButton {
    const productIds = this.checkoutProductIds.join(',');
    const couponCode = this.data.builderCategory[0]?.productCouponOverride || '';
    let checkoutUrl = `${this.env.get.xgritCheckoutUrl}?productId=${productIds}`;
    if (couponCode) {
      checkoutUrl += `&${this.globalCDIQueryParam}=${couponCode}`;
    }
    const checkoutLabel =
      this.checkoutProductIds.length === 0 ? 'Please select at least one product' : this.data.cartCtaLabel;
    return {
      bgTheme: 'bg-light',
      buttonLink: checkoutUrl,
      buttonAriaLabel: checkoutLabel,
      buttonText: checkoutLabel,
      buttonStyle: 'primary',
      useIcon: false,
      newTab: false,
      nofollow: false,
      noopener: false,
      noreferrer: false,
      fullWidth: true,
    };
  }

  get checkoutProductIds(): string[] {
    return this._checkoutProductIds;
  }

  get electiveClasses(): BundleSelectionMergedProduct[] {
    return this._electiveClasses;
  }

  get hasEnoughHours(): boolean {
    return this.bundleHours >= this.requiredHours;
  }

  get hasMultipleFormats(): boolean {
    return this.data?.builderCategory?.[0]?.bundleBuildCards?.some((card) => card.baseProducts.length > 1) ?? false;
  }

  get hourStatusClasses(): string {
    return this.hasEnoughHours ? 'border-vis-positive bg-vis-positive' : 'border-vis-negative bg-vis-negative';
  }

  get licencesSelected(): boolean {
    return this.deliveryMethod && this.deliveryMethod.length > 0 && this.checkoutSelectedProducts.length > 0;
  }

  get modalCheckoutButtonData(): VisionButton {
    const productIds = this.checkoutProductIds.join(',');
    const couponCode = this.data.builderCategory[0]?.productCouponOverride || '';
    let checkoutUrl = `${this.env.get.xgritCheckoutUrl}?productId=${productIds}`;
    if (couponCode) {
      checkoutUrl += `&${this.globalCDIQueryParam}=${couponCode}`;
    }

    return {
      bgTheme: 'bg-light',
      buttonLink: checkoutUrl,
      buttonAriaLabel: 'Proceed Anyway',
      buttonText: 'Proceed Anyway',
      buttonStyle: 'secondary',
      useIcon: false,
      newTab: false,
      nofollow: false,
      noopener: false,
      noreferrer: false,
      fullWidth: true,
    };
  }

  get requiredClassProductList(): BundleSelectionMergedProduct[] {
    return this._requiredClassProductList;
  }

  get requiredHours(): number {
    return this._requiredHours;
  }

  get selectedBaseProduct(): BundleSelectionMergedProduct | null {
    return this._selectedBaseProduct;
  }

  get totalCost(): number {
    return this._totalCost;
  }

  /**
   * Resets the bundle builder form to its initial state.
   *
   * This method performs the following actions:
   * - Deselects all bundle build cards in the first builder category.
   * - Hides the bundle search, filtering, and modal dialogs.
   * - Shows the schedule CTA.
   * - Hides the bundle product list.
   * - Clears the selected products for checkout.
   * - Resets the delivery method, search term, and selected form options.
   *
   */
  public resetBundleBuilderForm(): void {
    this.data.builderCategory[0].bundleBuildCards.forEach((card) => {
      card.selected = false;
    });
    this.showBundleSearch = false;
    this.showFiltering = false;
    this.showModal = false;
    this.hideScheduleCta = false;
    this.showBundleProductList = false;
    this.emptyStateMessage = '';
    this.checkoutSelectedProducts = [];
    this._bundleHours = 0;
    this._electiveClasses = [];
    this._checkoutProductIds = [];
    this._requiredClassProductList = [];
    this._selectedBaseProduct = null;
    this._totalCost = 0;
    this._requiredHours = 0;
    this.deliveryMethod = '';
    this.searchTerm = '';
    this.formSelectedOptions = [];
    this.checkoutNavigationStarted = false;
    this.updateBundleOptions();
    this.autoSelectSingleOption();
  }

  public markCheckoutNavigation(event: MouseEvent): void {
    const opensNewBrowsingContext =
      event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
    if (!opensNewBrowsingContext) {
      this.checkoutNavigationStarted = true;
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('pageshow', this.pageShowHandler);
    }
  }

  get totalCostFormatted(): string {
    return this.totalCost.toFixed(2);
  }

  get isMultiCard(): boolean {
    return this.data?.builderCategory?.[0]?.bundleBuildCards?.length > 1;
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('pageshow', this.pageShowHandler);
    }

    this.builderIndex = this.data.index;
    this.formTitle = this.redactor.bypassSanitizer(this.data.formTitle);
    this.topContent = this.redactor.bypassSanitizer(this.data.componentTitle);
    this.productListTitle = this.redactor.bypassSanitizer(this.data.productListTitle);
    this.requirementsTitle = this.redactor.bypassSanitizer(this.data.stateRequirementsTitle);
    this.electivesTitle = this.redactor.bypassSanitizer(this.data.electiveTitle);
    this.recommendedProductContent = this.redactor.bypassSanitizer(this.data.recommendedProductContent);
    this.hideScheduleCta = this.data.hideScheduleCta;
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
    this.updateBundleOptions();
    this.autoSelectSingleOption();
  }

  updateBundleHours(): void {
    const activeProducts = this.checkoutSelectedProducts.filter((product) => product.selected && product.productData);
    this._bundleHours = activeProducts.reduce((acc, product) => acc + product.productData.productHours, 0);
  }

  isElectiveSingleCard(): boolean {
    return !this.isMultiCard;
  }

  isRequiredSingleCard(): boolean {
    return !this.isMultiCard;
  }

  updateElectiveClasses(): void {
    this._electiveClasses = this.checkoutSelectedProducts.filter(
      (product) => product.section === 'elective' && product.productData,
    );
  }

  updateCheckoutProductIds(): void {
    this._checkoutProductIds = this.checkoutSelectedProducts
      .filter((product) => product.selected)
      .map((product) => product.craftId)
      .filter((value, index, self) => self.indexOf(value) === index);
  }

  updateRequiredClassProductList(): void {
    this._requiredClassProductList = this.checkoutSelectedProducts.filter((product) => product.section === 'required');
  }

  updateSelectedBaseProduct(): void {
    const filteringProducts = this.checkoutSelectedProducts.filter((product) => {
      if (product.section === 'base' && product.productData) {
        return product;
      }
    });
    if (filteringProducts.length === 0) {
      this._selectedBaseProduct = null;
    } else {
      this._selectedBaseProduct = filteringProducts.sort((a, b) => a.prio.localeCompare(b.prio))[0];
    }
  }

  updateTotalCost(): void {
    let cost = 0;
    if (this._selectedBaseProduct?.selected && this._selectedBaseProduct.productData) {
      cost = this._selectedBaseProduct.productData.cost;
    }
    this.checkoutSelectedProducts.forEach((product) => {
      if (product.selected && product.productData && product.section !== 'base') {
        cost += product.productData.cost;
      }
    });
    this._totalCost = cost;
  }

  updateRequiredHours(): void {
    let requiredHours = 0;
    if (this._selectedBaseProduct && this._selectedBaseProduct.productData) {
      requiredHours = this._selectedBaseProduct.productData.productHours;
    }
    this._requiredClassProductList.forEach((product) => {
      if (product.productData && product.required && product.section === 'required') {
        requiredHours += product.productData.productHours;
      }
    });
    if (this._selectedBaseProduct && requiredHours < this._selectedBaseProduct.cardHours) {
      requiredHours = this._selectedBaseProduct.cardHours;
    }
    this._requiredHours = requiredHours;
  }

  updateBundleData(): void {
    this.updateBundleHours();
    this.updateElectiveClasses();
    this.updateCheckoutProductIds();
    this.updateRequiredClassProductList();
    this.updateSelectedBaseProduct();
    this.updateTotalCost();
    this.updateRequiredHours();
  }

  updateBundleOptions(): void {
    if (this.showFiltering) {
      this._bundleOptions = this.data.builderCategory[0].bundleBuildCards.filter((card) =>
        card.entryState.toLowerCase().includes(this.searchTerm.toLowerCase()),
      );
    } else if (this.data.builderCategory.length === 0) {
      this._bundleOptions = [];
    } else {
      this._bundleOptions = this.data.builderCategory[0].bundleBuildCards;
    }
  }

  private autoSelectSingleOption(): void {
    if (this.bundleOptions.length === 1) {
      this.initialSelection(this.bundleOptions[0], true);
    }
  }

  addToCheckout(product: BundleSelectionMergedProduct, forceSelect?: boolean): void {
    if (forceSelect) {
      product.selected = true;
    }
    let index = -1;
    this.checkoutSelectedProducts.forEach((selectedProduct) => {
      if (selectedProduct.entryState === product.entryState && selectedProduct.craftId === product.craftId) {
        index = this.checkoutSelectedProducts.indexOf(selectedProduct);
      }
      if (product.section === 'base' && selectedProduct.section === 'base') {
        /*Check to see if the product being added has a higher prio than the selected product.
        If so, set the selected product to not selected.
        Otherwise, set the product being added to not selected.
        If the selected product deliveryType is not the same as the component deliveryMethod, set the selected product to not selected. (then the passed in product should be selected.)*/
        if (
          product.entryState === selectedProduct.entryState &&
          !this.deliveryMethod.includes(selectedProduct.deliveryType)
        ) {
          selectedProduct.selected = false;
        }
        if (product.prio === selectedProduct.prio && product.entryState === selectedProduct.entryState) {
          if (!this.deliveryMethod.includes(selectedProduct.deliveryType)) {
            selectedProduct.selected = false;
          }
        } else if (product.prio !== selectedProduct.prio && product.prio.localeCompare(selectedProduct.prio) < 0) {
          selectedProduct.selected = false;
        } else {
          product.selected = false;
        }
      }
    });
    if (index !== -1) {
      this.checkoutSelectedProducts[index] = product;
      this.updateBundleData();
      return;
    } else {
      const newProduct = { ...product };
      this.checkoutSelectedProducts.push(newProduct);
    }

    this.updateBundleData();
  }

  doFiltering(): void {
    this.showFiltering = true;
    this.updateBundleOptions();
  }

  initialSelection(bundleOption: BundleBuildCraftCards, forceAdd?: boolean): void {
    if (forceAdd) {
      bundleOption.selected = true;
    } else {
      bundleOption.selected = !bundleOption.selected;
    }
    const index = this.formSelectedOptions.findIndex((option) => option.entryState === bundleOption.entryState);
    if (index === -1) {
      this.formSelectedOptions.push(bundleOption);
    } else {
      this.formSelectedOptions.splice(index, 1);
    }
    if (!this.hasMultipleFormats && bundleOption.selected && bundleOption.baseProducts.length === 1) {
      this.deliveryMethod = `${bundleOption.baseProducts[0].deliveryType}-${this.builderIndex}`;
    }
    if (this.deliveryMethod) {
      this.setSelectedBundles();
    }
  }

  getProductLineItemsFromXgrit(): void {
    const couponCode = this.data.builderCategory[0].productCouponOverride || '';
    if (this.checkoutProductIds.length > 0) {
      const couponCodeList = couponCode ? [couponCode] : [];
      const apiProducts: ProductCodeLists[] = [{ productIdList: this.checkoutProductIds, couponCodeList }];
      this.xgritApi.getMultiProductPriceInfo(apiProducts).subscribe({
        next: (response: XgritPurchaseReceiptMultipriceResponse) => {
          if (response?.priceSetList?.[0]?.lineItemList?.length > 0) {
            this.mapLineItemsToSelectedProducts(response);
          } else {
            this.setEmptyStateMessage();
            this.errorHandler.handleError('Error getting product line items from Xgrit');
          }
        },
        error: () => {
          this.setEmptyStateMessage();
          this.errorHandler.handleError('Error getting product line items from Xgrit');
        },
      });
    } else if (this.formSelectedOptions.length > 0) {
      // State(s) selected and a delivery method chosen, but no matching products exist for that combination.
      this.setEmptyStateMessage();
    }
  }

  private setEmptyStateMessage(): void {
    const stateNames = this.formSelectedOptions.map((o) => o.entryState).join(', ');
    this.emptyStateMessage = stateNames
      ? `We don't currently have products available for ${stateNames}. Please select a different state.`
      : `We don't currently have products available for the selected state. Please select a different state.`;
  }

  private mapLineItemsToSelectedProducts(response: XgritPurchaseReceiptMultipriceResponse, addElectives?: boolean) {
    // Handle product selection based on rules
    this.checkoutSelectedProducts.forEach((product) => {
      // Set elective selection based on hours and addElectives flag
      if (product.section === 'elective') {
        if (addElectives && !this.hasEnoughHours) {
          product.selected = true;
        } else if (!addElectives) {
          product.selected = false;
        }
      }

      // Always populate productData regardless of selection state
      response.priceSetList[0].lineItemList.forEach((lineItem: LineItemList) => {
        if (product.craftId === lineItem.product._id) {
          product.productData = {
            productId: lineItem.product._id,
            productHours: lineItem.regulatorySeatTime,
            cost: lineItem.chargeAmount, // Use the actual charge amount (includes coupon discount)
            maxCost: lineItem.product.pricing.max,
            productName: lineItem.product.content.title,
            productDescription: lineItem.product.content.description,
            courseId: lineItem.product.courseId,
          };

          const fee = lineItem.product.sisterProductList.find(
            (sisterProduct) => sisterProduct.type === (ProductTypes.FEE as string),
          );
          if (fee) {
            product.productData.fee = fee.value;
          }
        }
      });
    });

    this.updateBundleData();

    // Check if we need to retry with electives
    if (!this.hasEnoughHours && !addElectives && this.isMultiCard) {
      this.mapLineItemsToSelectedProducts(response, true);
    } else {
      this.showBundleProductList = true;
      if (this.selectedBaseProduct?.deliveryType === 'webinar' && this.selectedBaseProduct.productData?.courseId) {
        this.xgritApi
          .getWebinarSchedule([{ courseid: this.selectedBaseProduct.productData.courseId }], {
            mortgageProductCategory: [
              {
                aceCategoryCode: 'MTG',
                title: 'Continuing Ed',
              },
            ],
          })
          .subscribe((webinarContent: WebinarScheduleResponse[]) => {
            this.selectedBaseProduct.webinarData = webinarContent;
          });
      }
    }
  }

  setSelectedBundles(): void {
    if (this.deliveryMethod === '') {
      // escape if deliveryMethod is not set
      return;
    }
    if (this.showBundleProductList) {
      this.showBundleProductList = false;
    }
    this.emptyStateMessage = '';
    this.checkoutSelectedProducts = [];
    this._electiveClasses = [];
    this._checkoutProductIds = [];
    this._requiredClassProductList = [];
    this._selectedBaseProduct = null;
    this._totalCost = 0;
    this._requiredHours = 0;
    this.formSelectedOptions.forEach((option) => {
      const hidePills = option.baseProducts.length === 1 && option.baseProducts[0].deliveryType === 'online';
      option.baseProducts.forEach((product) => {
        if (this.deliveryMethod.includes(product.deliveryType)) {
          const selectedProduct: BundleSelectionMergedProduct = {
            deliveryType: product.deliveryType,
            hideScheduleCta: this.hideScheduleCta,
            section: 'base',
            multipleRegulators: false,
            selected: true,
            required: true,
            entryState: option.entryState,
            moreInfo: option.showMoreInfo ? option.moreInfo : '',
            prio: option.baseProductPrio,
            craftId: product.productId,
            cardHours: option.minHours,
            hidePills,
          };
          this.addToCheckout(selectedProduct);
        }
      });
      option.electiveProducts.forEach((product) => {
        if (product.productId.length > 0) {
          const selectedProduct: BundleSelectionMergedProduct = {
            deliveryType: 'online',
            section: 'elective',
            hideScheduleCta: this.hideScheduleCta,
            multipleRegulators: false,
            selected: true,
            required: false,
            entryState: option.entryState,
            prio: option.baseProductPrio,
            craftId: product.productId,
            cardHours: option.minHours,
            hidePills,
          };
          this.addToCheckout(selectedProduct);
        }
      });
      option.requiredProducts.forEach((product) => {
        if (product.productId.length > 0) {
          const selectedProduct: BundleSelectionMergedProduct = {
            deliveryType: 'online',
            hideScheduleCta: this.hideScheduleCta,
            section: 'required',
            multipleRegulators: product.multipleRegulators,
            selected: true,
            required: true,
            entryState: option.entryState,
            prio: option.baseProductPrio,
            craftId: product.productId,
            cardHours: option.minHours,
            hidePills,
          };
          this.addToCheckout(selectedProduct, true);
        }
      });
    });
    this.getProductLineItemsFromXgrit();
  }

  toggleBundleSearch(forceState?: string): void {
    if (forceState) {
      this.showBundleSearch = forceState === 'open';
      return;
    } else {
      this.showBundleSearch = !this.showBundleSearch;
    }
    if (!this.showBundleSearch) {
      this.searchTerm = '';
      this.updateBundleOptions();
    }
  }
}
